/**
 * PipedriveLayout Component
 * Layout wrapper for Pipedrive pages with separate sidebar
 */

import { Outlet, Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { PipedriveSidebar } from '@/components/PipedriveSidebar'
import { ViewSwitcher } from '@/components/ViewSwitcher'
import { EnvironmentBadge } from '@/components/EnvironmentBadge'
import { useSocket } from '@/hooks/use-socket'
import { Menu, Search } from 'lucide-react'
import { useState, useEffect } from 'react'
import logo from '@/assets/GrackleDocs_BirdOnly.png'
import { UserMenu } from '@/components/UserMenu'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { pipedriveNavigationGroups } from '@/config/pipedriveNavigationConfig'

export default function PipedriveLayout() {
  const location = useLocation()
  const { isConnected } = useSocket()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('gracklecrm_dark_mode') === 'true'
  })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
    localStorage.setItem('gracklecrm_dark_mode', String(darkMode))
  }, [darkMode])

  // Flatten navigation for mobile
  const mobileNavItems = pipedriveNavigationGroups.flatMap((group) => {
    if (group.standalone && group.href) {
      return [{ name: group.label, href: group.href }]
    }
    if (group.items) {
      return group.items.map((item) => ({ name: item.label, href: item.href }))
    }
    return []
  })

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 gap-4">
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Mobile Navigation Drawer Trigger */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  aria-label="Toggle navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-[#B45309]" />
                    <span>Pipedrive Navigation</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-6" aria-label="Mobile navigation">
                  <div className="space-y-1">
                    {mobileNavItems.map((item) => {
                      const isActive = location.pathname === item.href
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setMobileNavOpen(false)}
                          className={cn(
                            'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            isActive
                              ? 'bg-[#B45309] text-white'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          )}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {item.name}
                        </Link>
                      )
                    })}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2">
              <img src={logo} alt="Grackle CRM" className="h-8 w-8 object-contain" />
              {/* The second shell. This layout has no banner region at all, so without
                  this the Pipedrive pages would be the one place the badge vanishes -
                  which is worse than not having it, because its absence means production. */}
              <EnvironmentBadge />
              <ViewSwitcher />
            </div>
          </div>

          {/* Simple Search (Pipedrive-specific) */}
          <div className="flex-1 max-w-xl md:max-w-2xl mx-2 md:mx-auto">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Search Pipedrive data..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full"
                aria-label="Search Pipedrive data"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <UserMenu
              darkMode={darkMode}
              onToggleDarkMode={() => setDarkMode((prev) => !prev)}
              isConnected={isConnected}
              onLogIncomingCall={() => {}}
            />
          </div>
        </div>
      </header>

      {/* Desktop Sidebar Navigation */}
      <div className="hidden lg:block">
        <PipedriveSidebar />
      </div>

      {/* Main content */}
      <main id="main-content" className="p-4 sm:p-6 lg:p-8 lg:pl-[272px]" role="main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  )
}
